import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { KirokuMenu, RecordDetail } from '@app/models/record.model';
import { AccesslogService } from '@app/services/accesslog.service';
import { ApiService, CareConneRelateRes } from '@app/services/api.service';
import { DateService } from '@app/services/date.service';
import { DialogService } from '@app/services/dialog.service';
import { LibService } from '@app/services/lib.service';
import { MenuService } from '@app/services/menu.service';
import { MessageService } from '@app/services/message.service';
import { RecordService } from '@app/services/record.service';
import { Store } from '@ngxs/store';
import { NoteDisplayInputParams } from '@care-connect-japan/ckweblib/lib/data/schema/params/library-params';
import { Observable, combineLatest, delay, firstValueFrom, map } from 'rxjs';
import * as moment from 'moment';
import { UserService } from '@app/services/dai.service';
import { StorageEnum } from '@app/shared/enum/storage';
import { MenuItem } from '@app/models/menu.model';
import { DialogData } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { SettingSelectors } from '@app/store/setting/setting.selectors';
import { TriggerHistory } from '@app/store/schedule/schedule.classes';
import { timeFormat } from '@app/basic/note-list/config';
import { PictureDTO } from '@care-connect-japan/ckweblib/lib/data/schema/data-model/dto/picture';
import { FormControl, FormGroup } from '@angular/forms';
import { WebviewService } from '@app/services/webview.service';
import { LibNoteService } from '@care-connect-japan/ckweblib';
import { NoteBlobService } from '@app/services/noteblob.service';
import { CareConneSenderCreate } from '@app/store/record/record.classes';
import { AccessLog } from '@app/shared/enum/record-list.enum';
import { Location } from '@angular/common';
import { CareConneService } from '@app/services/care-conne/care-conne.service';

interface PageParam {
  objkbn: string;
  objcd: string;
  noteid: number;
}

@Component({
  selector: 'app-record-detail',
  templateUrl: './record-detail.component.html',
  styleUrls: ['./record-detail.component.scss'],
})
export class RecordDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('container') containerEl?: ElementRef<HTMLElement> | null = null;
  @ViewChildren('libEl') libEl!: QueryList<any>;
  record!: RecordDetail;
  previous: PageParam | null = null;
  noteParam?: NoteDisplayInputParams = undefined;
  writable$!: Observable<boolean>;
  writable!: boolean;
  disabled?: boolean;
  form!: FormGroup;
  hasTitle!: (record: RecordDetail) => boolean;
  imgLinks: string[] = [];

  private _isChanging: boolean = false;
  private _isSubmit: boolean = false;
  isLoading: boolean = false;
  // img viewer
  imageSrc?: string;

  constructor(
    private careConneService: CareConneService,
    private location: Location,
    private libNoteService: LibNoteService,
    private noteblobService: NoteBlobService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private accesslog: AccesslogService,
    private message: MessageService,
    private dialog: DialogService,
    private snackBar: MatSnackBar,
    private dateService: DateService,
    private apiService: ApiService,
    private menuService: MenuService,
    private libService: LibService,
    private recordService: RecordService,
    private webviewService: WebviewService,
    private cdr: ChangeDetectorRef
  ) {
  }
  ngAfterViewInit(): void {
    this.libEl.changes
      .pipe(delay(500))
      .subscribe(() => {
        this.imgBinder();
      })
  }


  get pickup() {
    return this.record.pickupflg == 1;
  }

  ngOnDestroy(): void {
    this.webviewService.kidsWarningBeforeChangePageIndicator(false);
  }

  get daiName() {
    return this.userService.daiInfo(this.record.objcd)?.name || this.record.objnm;
  }

  get startDate() {
    return this.form?.get('startDate') as FormGroup;
  }

  get endDate() {
    return this.form?.get('endDate') as FormGroup;
  }

  get handover() {
    return this.form?.get('handover') as FormControl;
  }
  get isChanging() {
    return this._isChanging;
  }
  get isSubmit() {
    return this._isSubmit;
  }

  get currentMenu(): string {
    return localStorage.getItem(StorageEnum.MENU_ITEM) || KirokuMenu.EMPTY;
  }

  get isCareConneFeat() {
    return this.store.selectSnapshot(SettingSelectors.isCareConneFeature);
  }

  async sendToCareConne() {
    // create message
    try {
      if (!this.isCareConneFeat) return;
      let isCreateMessage = this.dataCollect();
      if (!isCreateMessage) return;
      let daicd = this.record.objcd;
      let roomData = await this.careConneService.pickRooms(daicd);
      if (!roomData) return;
      //
      this.webviewService.triggerSpinnerMobile(true);
      // refetch image from s3
      let note = this.noteParam?.noteData;
      let observableNote = this.noteblobService.fetchAndMapNoteBlobs([note]);
      let noteFinal = await firstValueFrom(observableNote[0]);
      let links = noteFinal?.noteblobs?.map((x: PictureDTO) => x.link ?? '') ?? [];
      let res = await this.careConneService.sendMessageToS3(roomData!.message, roomData!.roomIds, links);
      if (!res.status) {
        throw (res)
      }
      this.careConneService.sendSuccessDialog();
    } catch (error: any | CareConneRelateRes) {
      let message = error?.message || '';
      this.careConneService.sendFailDialog(message);

    } finally {
      this.webviewService.triggerSpinnerMobile(false);
    }


  }

  private dataCollect() {
    if (!this.containerEl) return false;
    let el = this.containerEl.nativeElement;
    const rows = el.getElementsByClassName('info-row');

    const name = this.daiName + '様';
    const titleNm = this.record.ttlnm;
    const recordTime = moment(this.record.sdate, timeFormat).locale('ja').format('YYYY年MM月DD日（ddd） HH:mm')
    const msg: string[] = [`${name}　${titleNm}`, recordTime];
    for (let row of Array.from(rows)) {
      const label = (row.getElementsByClassName('info-label')[0]?.firstChild as any)?.innerText as string;
      let value = (row.getElementsByClassName('info-content')[0]?.firstChild as any)?.innerText as string;
      if (!value) {
        value = (row.getElementsByClassName('checkbox')[0]?.lastChild as any)?.innerText as string;
      }
      if (value) {
        const str = `【${label}】${value}`;
        msg.push(str);
      }
    }
    let message = msg.join(`\n`);
    this.store.dispatch(new CareConneSenderCreate([], message));
    return true

  }

  // get isCasefile() {
  //   return this.currentMenu === MENU_POCKET.CASEFILE;
  // }

  imgBinder() {
    const imgs = document.querySelectorAll('lib-note-detail lib-note-detail-mobile img');
    imgs.forEach(img => {
      img.addEventListener('click', (event: any) => this.imgHander(event));
      this.imgLinks.push((img as any).src);
    });
  }

  imgHander(event: PointerEvent) {
    event.preventDefault();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { image: true }
    });
    const target = event.target as any;
    this.imageSrc = target?.src || null
  }
  unbindImg() {
    if (this.route.snapshot.queryParams['image']) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { image: true },
      }).then(() => history.back());
    }
    this.imageSrc = undefined;
  }

  forceBlur(event: any) {
    // debugger
    const target = event.target;
    target.blur();
    setTimeout(target.focus(), 500);

  }

  libNoteFetch() {
    const noteDatas = this.libNoteService.getNoteData();
    if (this.recordService.isValidationError(noteDatas)) return;
    const findNoteData = this.recordService.findNoteData(noteDatas);
    // const getStaffName = this.scheduleService.getStaffName(this.staffList);
    const noteData = findNoteData(this.record) as any;
    return noteData;
  }

  dataChangingEvent() {
    setTimeout(() => {
      const noteData = this.libNoteFetch();
      if (noteData) {
        const sdate = this.dateService.parseDateForm(this.startDate.value as any) || this.dateService.formatISO8601(this.record.sdate);
        const edate = this.dateService.parseDateForm(this.endDate.value as any) || this.dateService.formatISO8601(this.record.edate);
        this._isChanging = this.record.v2contents != noteData.v2contents
          || noteData.noteblobs
          || this.record.sdate as any != sdate || this.record.edate as any != edate
          || this.record.pickupflg != this.handover.value;
      }
      if (this._isChanging) {
        this.webviewService.kidsWarningBeforeChangePageIndicator(true);
      }

    }, 300)
  }

  async ngOnInit(): Promise<void> {
    const titleSetList = this.store.selectSnapshot(SettingSelectors.titleTagList);
    const hasTitle = this.recordService.hasTitle(titleSetList);
    this.hasTitle = (record: RecordDetail) =>
      record.notekbn !== 0 || hasTitle(record);
    combineLatest([this.route.data, this.route.params])
      .pipe(
        map(([data, params]) => ({ data, params })),
      ).subscribe(async ({ data }) => {
        const { note, displayOption } = data['record'];
        this.record = note;
        this.noteParam = { ... await this.libService.getNoteParam(this.record), showLimit: -1 }
        this.disabled = displayOption?.isLocked;
        this.writable = !displayOption || displayOption.writable;
        this.form = this.recordService.setRecordForm(null, this.record);
        this.cdr.detectChanges();
      });

    const menuList = this.store.selectSnapshot(SettingSelectors.menuList);
    const getMenuItem = this.menuService.getMenuItem(menuList);

    this.writable$ = this.route.paramMap.pipe(
      map((paramMap) => {
        const menuGroupId = paramMap.get('menuGroupId');
        const menuid = paramMap.get('menuid');
        const type = paramMap.get('type');

        const menuItem = getMenuItem(menuGroupId, menuid, type);
        return menuItem?.write ?? false;
      })
    );
    this.imgLocationPop();
  }

  imgLocationPop() {
    this.unbindImg();
    this.location.subscribe((event: any) => {
      if (event.type === 'popstate') {
        if (this.imageSrc) {
          this.imageSrc = undefined;
        }
      }
    });
  }

  truncate(text: string, count: number): string {
    return text?.length > count ? `${text.slice(0, count)}...` : text;
  }

  editLink(operation = '') {
    const { objkbn, objcd, noteid } = this.record;
    const path = 'record-edit';
    return [
      '',
      {
        outlets: {
          edit: [this.editPage(path), noteid, { objkbn, objcd, operation }],
        },
      },
    ];
  }

  delete() {
    this.recordService.delete(this.record, this.daiName).subscribe(() => {
      const operation = this.recordService.getOperation(this.record.noteid);
      const sdate = this.dateService.formatISO8601(this.record.sdate);
      const edate = this.dateService.formatISO8601(this.record.edate);
      const note = {
        ...this.record,
        sdate,
        edate,
        delstat: 1,
      };
      const displayName = this.accesslog.getKidsDisplayName(AccessLog.RECORD_DELETE);

      this.apiService
        .setNoteList([{ operation, note, displayName }])
        .subscribe((res) => {
          const errorList = res
            .filter((item) => !!item.error)
            .map((item) => this.message.getErrorMessage(item.error!));

          if (errorList.length > 0) {
            this.dialog.message(errorList.join('\n'));
          } else if (res.length > 0) {
            // this.snackBar.open('記録を削除しました');
            this.confirmDelete().afterClosed().subscribe(() => {
              this.store.dispatch(new TriggerHistory());
              this.router.navigate(['/home/list'], { replaceUrl: true });
              // if(this.isCasefile) {
              //   history.go(-1);
              // } else {
              //   this.router.navigate(['/home/list'], { replaceUrl: true });
              // }
            })
          }
        });
    });
  }

  confirmDelete() {
    const data: DialogData = {
      cancelText: '',
      excuteText: '閉じる',
      content: `<p>記録を削除しました</p>`,
      style: {
        'text-align': 'center'
      }

    };
    return this.dialog.confirm(data);
  }

  protected backPage(path: string) {
    return path;
  }

  protected editPage(path: string) {
    return path;
  }

  onChange() {
    console.log(this.form);
  }

  navigateToPrevPath() {
    this.isLoading = true;
    try {
      const selectedMenuData = JSON.parse(localStorage.getItem("selectedMenuData") ?? '') as MenuItem;
      const prevToPath = '/home' + selectedMenuData.path;
      this.router.navigate([prevToPath], { replaceUrl: true }).finally(() => { history.go(-1) });
    } catch (error) {
      this.router.navigate(['/home/list'], { replaceUrl: true }).finally(() => { history.go(-1) });
    }
  }
}
